import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { ArrowLeft, Mail } from 'lucide-react';
import { toast } from 'react-toastify';
import { getAuth, sendSignInLinkToEmail } from 'firebase/auth';
import { buildTenantPath, extractTenantSlugFromPathname } from '../../utils/tenantSlug';

interface EmailLinkLoginForm {
  email: string;
}

const EmailLinkLoginPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const { register, handleSubmit, formState: { errors } } = useForm<EmailLinkLoginForm>();
  const tenantSlug =
    typeof window !== 'undefined'
      ? extractTenantSlugFromPathname(window.location.pathname)
      : null;

  const onSubmit = async (data: EmailLinkLoginForm) => {
    setIsLoading(true);
    const auth = getAuth();

    const actionCodeSettings = {
      url: `${window.location.origin}${buildTenantPath('/login/complete', tenantSlug)}`,
      handleCodeInApp: true
    };

    try {
      await sendSignInLinkToEmail(auth, data.email, actionCodeSettings);
      window.localStorage.setItem('emailForSignIn', data.email);
      setEmailSent(true);
      toast.success('Link de acesso enviado com sucesso!');
    } catch (error: any) {
      console.error('Error:', error);
      if (error.code === 'auth/invalid-email') {
        toast.error('Email inválido');
      } else if (error.code === 'auth/too-many-requests') {
        toast.error('Muitas tentativas. Tente novamente mais tarde');
      } else {
        toast.error('Erro ao enviar link de acesso');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 py-20">
      <div className="container mx-auto px-4">
        <div className="max-w-md mx-auto">
          <Link
            to={buildTenantPath('/login', tenantSlug)}
            className="inline-flex items-center text-sm text-neutral-600 hover:text-primary-600 mb-8"
          >
            <ArrowLeft size={16} className="mr-1" />
            Voltar para login
          </Link>

          <div className="bg-white rounded-lg shadow-sm p-8">
            <div className="text-center mb-8">
              <h1 className="text-2xl font-display font-bold text-neutral-900 mb-2">
                Login por Email
              </h1>
              <p className="text-neutral-600">
                Digite seu email para receber um link de acesso seguro
              </p>
            </div>

            {!emailSent ? (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-neutral-700 mb-1">
                    Email
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail size={18} className="text-neutral-500" />
                    </div>
                    <input
                      id="email"
                      type="email"
                      className="block w-full pl-10 pr-3 py-2 border border-neutral-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                      {...register('email', {
                        required: 'Email é obrigatório',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Email inválido'
                        }
                      })}
                    />
                  </div>
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                >
                  {isLoading ? (
                    <span className="inline-block h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  ) : (
                    'Enviar Link de Acesso'
                  )}
                </button>
              </form>
            ) : (
              <div className="text-center">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                  <p className="text-green-800">
                    Um link de acesso foi enviado para seu email.
                    Por favor, verifique sua caixa de entrada.
                  </p>
                </div>
                <p className="text-sm text-neutral-600 mb-4">
                  Não recebeu o email? Verifique sua pasta de spam ou tente novamente.
                </p>
                <div className="space-y-3">
                  <button
                    onClick={() => setEmailSent(false)}
                    className="w-full py-2 px-4 border border-neutral-300 rounded-md shadow-sm text-sm font-medium text-neutral-700 bg-white hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    Tentar Novamente
                  </button>
                  <Link
                    to={buildTenantPath('/login', tenantSlug)}
                    className="inline-block w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    Voltar para Login
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailLinkLoginPage;
